import {
  Alert,
  Badge,
  Box,
  Flex,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router";

import { useAdminLinearCleanupIssues } from "@/admin/data/useAdminLinearCleanupIssues";
import TimestampCell from "@/common/components/TimestampCell";
import { LinearIssueStateBadge } from "@/feature/components/LinearIssueStateBadge";
import pluralize from "@reflag/shared/utils/pluralize";

import { LinearCleanupCreationStatusBadge } from "./LinearCleanupCreationStatusBadge";

export function AdminOrgLinearCleanupIssues({ orgId }: { orgId: string }) {
  const { data = [], isLoading, isError } = useAdminLinearCleanupIssues(orgId);
  if (isLoading) return <Spinner />;
  if (isError)
    return <Alert status="error">Could not load Linear cleanup issues.</Alert>;
  if (!data.length)
    return (
      <Text color="dimmed">
        No Linear cleanup attempts recorded yet. History starts with new
        attempts.
      </Text>
    );

  return (
    <Box overflowX="auto">
      <Text color="dimmed" fontSize="sm" mb={3}>
        Most recent 100 creation attempts.
      </Text>
      <Table>
        <Thead>
          <Tr>
            <Th>Flag</Th>
            <Th>Issue</Th>
            <Th>Creation result</Th>
            <Th>Linear state</Th>
            <Th>Created</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((issue) => (
            <Tr key={issue.id}>
              <Td>
                {issue.feature.url ? (
                  <Link as={RouterLink} to={issue.feature.url}>
                    {issue.feature.key ?? issue.feature.name}
                  </Link>
                ) : (
                  issue.feature.name
                )}
                <Text color="dimmed" fontSize="xs">
                  {issue.feature.appName}
                </Text>
              </Td>
              <Td>
                <Link
                  as={RouterLink}
                  to={`/admin/${orgId}/linear-cleanup-issues/${issue.id}`}
                >
                  <Text fontWeight="semibold">
                    {issue.details?.identifier ?? "View attempt"}
                  </Text>
                  <Text noOfLines={1} maxW="xs">
                    {issue.title}
                  </Text>
                </Link>
              </Td>
              <Td>
                <Flex gap={2} align="center" wrap="wrap">
                  <LinearCleanupCreationStatusBadge status={issue.status} />
                  {!!issue.warningCount && (
                    <Badge colorScheme="yellow">
                      {issue.warningCount}{" "}
                      {pluralize("warning", issue.warningCount)}
                    </Badge>
                  )}
                </Flex>
              </Td>
              <Td>
                {issue.deletedAt ? (
                  <Badge>Deleted</Badge>
                ) : issue.status === "created" ? (
                  <LinearIssueStateBadge state={issue.details?.state ?? null} />
                ) : (
                  "—"
                )}
              </Td>
              <Td>
                <TimestampCell value={issue.createdAt} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
